<template>
  <dx-data-grid
    ref="dataGrid"
    :data-source="store"
    :remote-operations="remoteOperations"
    :columns="columns"
    :filter-value="filterValue"
    :column-auto-width="true"
    :show-borders="true"
    :show-row-lines="false"
    :show-column-lines="true"
    :row-alternation-enabled="true"
    @row-prepared="onRowPrepared"
  >
    <dx-sorting mode="none"/>

    <dx-paging :page-size="50"/>
    <dx-pager
      :allowed-page-sizes="[15, 20, 50]"
      :visible="true"
      :show-info="true"
      :show-navigation-buttons="true"
      :show-page-size-selector="true"
    />
  </dx-data-grid>
</template>

<script>
import { DxDataGrid } from 'devextreme-vue';
import { DxPager, DxPaging, DxSorting } from 'devextreme-vue/data-grid';
import { get } from 'lodash';
import createBackendStore from '@/lib/create-backend-store';
import { buildColumns } from '@/lib/data-grid-columns';
import addRowPriorityClass from '@/lib/dx/add-row-priority-class';

export default {
  name: 'StationGrid',
  components: {
    DxDataGrid,
    DxPager,
    DxPaging,
    DxSorting,
  },
  data() {
    return {
      columns: [],
      store: null,
      remoteOperations: {
        paging: true,
        filtering: true,
        sorting: true,
      },
    };
  },
  props: {
    filterValue: { type: Array, default: null },
    station: { type: String, default: '' },
    showStatus: { type: Object, default: null },
    nextStatus: { type: Object, default: null },
    sortBy: { type: Object, default: null },
  },
  computed: {
    dataGrid() {
      return this.$refs.dataGrid.instance;
    },
  },
  watch: {
    columns: {
      immediate: true,
      handler() {
        if (this.sortBy) {
          this.$nextTick(() => {
            this.dataGrid.clearSorting();

            Object.entries(this.sortBy)
              .forEach(([dataField, order], index) => {
                this.dataGrid.columnOption(dataField, 'sortOrder', order);
                this.dataGrid.columnOption(dataField, 'sortIndex', index);
              });
          });
        }
      },
    },
  },
  methods: {
    buildColumns,
    configureColumns() {
      return [
        ...this.buildColumns('manufacturingItem', [
          {
            property: 'id',
          },
          {
            property: 'shipment.orderEid',
            entity: 'shipment',
            captionContext: 'short',
          },
          {
            property: 'shipment.acceptanceNumber',
            entity: 'shipment',
            captionContext: 'short',
          },
          {
            property: 'manufacturerId',
            visible: false,
          },
          {
            property: 'priority',
            visible: false,
          },
          {
            property: 'statusId',
            visible: false,
          },
          {
            property: 'planWeek',
            captionContext: 'short',
          },
          ...this.showStatus ? [{
            property: `statusEventMap[${this.showStatus.id}].occurredAt`,
            entity: 'statusEvent',
            name: 'statusOccurredAt',
            caption: this.$t(`entities.status.values.name.${this.showStatus.name}`),
            alignment: 'center',
          }] : [],
          {
            property: 'quantity',
          },
          {
            property: 'productNameId',
            captionContext: 'short',
          },
          {
            property: 'productCategoryId',
            captionContext: 'short',
          },
          {
            property: 'complaintTypeId',
            captionContext: 'short',
          },
          {
            property: 'materialId',
          },
          {
            property: 'lamellaTypeId',
            captionContext: 'short',
            valueContext: 'short',
          },
          {
            property: 'plateThickness',
            captionContext: 'short',
          },
          {
            property: 'legProfile',
            captionContext: 'short',
          },
          {
            property: 'shipment.customer',
            entity: 'shipment',
          },
          ...get(this.showStatus, 'name') === 'packaged' ? [{
            property: 'packageCount',
            captionContext: 'short',
          }] : [],
          ...this.station === 'packaging' ? [{
            property: 'deliverToId',
          }] : [],
        ]),
        ...this.nextStatus ? [{
          type: 'buttons',
          buttons: [{
            icon: 'todo',
            hint: this.$t('components.stationGrid.pushStatusHint'),
            onClick: event => this.$emit('click:push-status', event),
          }],
          width: 40,
        }] : [],
      ];
    },
    configureStore(columns) {
      const fields = columns
        .map(column => column.dataField)
        .filter(f => f);

      return createBackendStore('/manufacturing-items', { fields, onLoaded: this.onLoaded });
    },
    refresh() {
      this.dataGrid.refresh();
    },
    onRowPrepared: addRowPriorityClass,
    onLoaded() {
      this.$emit('loaded');
    },
  },
  created() {
    this.columns = this.configureColumns();
    this.store = this.configureStore(this.columns);
  },
};
</script>

<style scoped>

</style>
